import React, { ForwardedRef, forwardRef, useEffect, useState, KeyboardEvent, MouseEvent, useRef } from "react";
import './Dropdown.css';
import IconButton from "./IconButton";
import { Link } from "react-router-dom";
import getclassNames from "../../utils/getClassNames";

interface DropdownProps {
    id: string;
    options: { label: string, href?: string, onClick?: () => void, newWindow?: boolean; disabled?: boolean; className?: string; }[];
    openTop?: boolean;
    disabled?: boolean;
    optionSize?: 'small' | 'medium' | 'large' | 'xlarge';
    alignRight?: boolean;
    buttonLabel?: string;
    buttonClassName?: string;
}

const Dropdown = forwardRef((props: DropdownProps, ref?: ForwardedRef<any>) => {
    const [visible, setVisible] = useState(false);
    const triggeredToggle = useRef<boolean>(false);

    useEffect(() => {
        if (visible) {
            const closeDropdownHandler = () => {
                if (triggeredToggle.current) {
                    triggeredToggle.current = false;
                } else {
                    setVisible(false);
                }
            };

            document.addEventListener('click', closeDropdownHandler);

            return () => {
                document.removeEventListener('click', closeDropdownHandler);
            }
        }
    }, [visible]);

    const handleDropdownOpen = () => {
        triggeredToggle.current = true;
        setVisible(!visible);
    };

    const handleOptionOnKeyUp = (e: KeyboardEvent<HTMLDivElement>, onClick?: () => void) => {
        if (!onClick) {
            return;
        }

        if (e.code === 'Space' || e.code === 'Enter') {
            onClick();
        }
    };

    const handleContainerKeyUp = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.code === 'Escape') {
            setVisible(false);
        }
    };

    return (
        <div className={getclassNames('dropdown-container', props.buttonLabel ? 'has-button' : '')} ref={ref} onKeyUp={handleContainerKeyUp}>
            {props.buttonLabel ? (
                <button 
                    type="button"
                    id={props.id}
                    className={getclassNames('button dropdown-button', props.buttonClassName || '')}
                    onClick={handleDropdownOpen}
                    disabled={props.disabled}
                >
                    <span className="button-text">{props.buttonLabel}</span>
                    <i className={getclassNames('ri-arrow-down-s-line', visible ? 'dropdown-arrow-up' : '')}></i>
                </button>
            ) : (
                <IconButton className="flat" 
                    id={props.id} 
                    onClick={handleDropdownOpen} 
                    icon={<i className="ri-more-2-line"></i>} />
            )}
            {visible && <div className={getclassNames('dropdown-contents', props.openTop ? `top-${props.options.length}` : '', props.alignRight ? 'align-right' : '')}>
                {props.options.map((o, i) => {
                    if (props.disabled || o.disabled) {
                        return <div key={i} tabIndex={0} className={getclassNames('dropdown-option disabled', props.optionSize ?? 'medium', o.className || '')}>{o.label}</div>
                    }

                    if (o.href) {
                        return <Link key={i} to={o.href} target={o.newWindow ? '_blank' : '_self'} className={getclassNames('dropdown-option', props.optionSize ?? 'medium', o.className || '')} tabIndex={0}>{o.label}</Link>
                    }

                    return <div key={i} onKeyUp={(e) => handleOptionOnKeyUp(e, o.onClick)} tabIndex={0} className={getclassNames('dropdown-option', props.optionSize ?? 'medium', o.className || '')} onClick={o.onClick}>{o.label}</div>
                })}
            </div>}
        </div>
    )
});

export default Dropdown;
